/**
 * IndustryBenchmarksPanel — shared component that surfaces an industry's
 * aggregated financial benchmarks (peer medians) wherever a Case workflow
 * touches the industry.
 *
 * Two render modes:
 *   - `summary`: compact 3-card view (EBITDA Margin, ROIC vs WACC, EV/EBITDA).
 *     Used inline inside wizard steps (e.g. Step 3 Competition Analysis) where
 *     the user is making a decision and just needs at-a-glance peer context.
 *   - `full`: delegates to the existing <IndustryFinancialBenchmarks /> which
 *     renders the same overview / EV-insights / revenue-and-capital sub-tabs
 *     used on IndustryDetail.tsx. Used inside CaseDetail "Benchmarks" tab.
 *
 * Returns null when industryId is missing so callers can mount unconditionally.
 */
import { Activity, Target, Landmark, AlertCircle } from 'lucide-react';
import { useIndustryBenchmarks } from '../../lib/api/industryResearchApi';
import { formatPercent } from '../financial/utils/formatters';
import IndustryFinancialBenchmarks from '../industry/IndustryFinancialBenchmarks';

interface IndustryBenchmarksPanelProps {
  industryId: string | null | undefined;
  mode?: 'summary' | 'full';
}

interface SummaryCardProps {
  label: string;
  value: string;
  sub?: string;
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
}

function SummaryCard({ label, value, sub, iconBg, iconColor, icon }: SummaryCardProps) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-3.5">
      <div className="flex items-start justify-between mb-2">
        <p className="text-[11px] text-neutral-500 font-medium uppercase tracking-wide leading-tight">
          {label}
        </p>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ml-2 ${iconBg}`}>
          <span className={iconColor}>{icon}</span>
        </div>
      </div>
      <p className="text-lg font-bold text-neutral-900 leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-neutral-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function IndustryBenchmarksPanel({
  industryId,
  mode = 'summary',
}: IndustryBenchmarksPanelProps) {
  if (!industryId) return null;

  if (mode === 'full') {
    return <IndustryFinancialBenchmarks industryId={industryId} />;
  }

  // ── Summary mode ──────────────────────────────────────────────────────
  return <SummaryView industryId={industryId} />;
}

function SummaryView({ industryId }: { industryId: string }) {
  const { data, isLoading, error } = useIndustryBenchmarks(industryId);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 animate-pulse">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-white border border-neutral-200 rounded-xl p-3.5 space-y-2">
            <div className="h-2.5 bg-neutral-100 rounded w-3/5" />
            <div className="h-5 bg-neutral-200 rounded w-2/3" />
            <div className="h-2 bg-neutral-100 rounded w-4/5" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <p className="text-xs">
          Industry benchmarks unavailable. Process market leaders through Company Research to enable peer context.
        </p>
      </div>
    );
  }

  const summary = data?.latest_summary;
  if (!data || !data.success || !summary) {
    return (
      <div className="flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-neutral-600">
        <AlertCircle className="h-4 w-4 shrink-0 text-neutral-400" />
        <p className="text-xs">
          No peer benchmarks yet — link market leaders to Company Research from the industry's competitive-landscape view.
        </p>
      </div>
    );
  }

  const roicAboveWacc =
    summary.roic != null && summary.wacc != null && summary.roic > summary.wacc;
  const spread =
    summary.roic != null && summary.wacc != null
      ? formatPercent(summary.roic - summary.wacc)
      : null;
  const ebitdaSub =
    data.companies_count > 0
      ? `Median · ${data.companies_count} co${data.companies_count !== 1 ? 's' : ''}`
      : undefined;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-neutral-900">
          Industry Benchmarks
          <span className="ml-2 text-xs font-normal text-neutral-500">
            {data.industry_name}
          </span>
        </h4>
        <span className="text-[11px] text-neutral-400 bg-neutral-50 border border-neutral-200 px-2 py-0.5 rounded-full">
          {data.region.country} · {data.years_available}y
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          label="Peer EBITDA Margin"
          value={formatPercent(summary.ebitda_margin)}
          sub={ebitdaSub}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
          icon={<Activity className="h-3.5 w-3.5" />}
        />
        <SummaryCard
          label="Peer ROIC"
          value={formatPercent(summary.roic)}
          sub={
            spread
              ? `${roicAboveWacc ? 'Above' : 'Below'} WACC by ${spread.replace('-', '')}`
              : 'vs WACC'
          }
          iconBg={roicAboveWacc ? 'bg-green-50' : 'bg-red-50'}
          iconColor={roicAboveWacc ? 'text-green-600' : 'text-red-500'}
          icon={<Target className="h-3.5 w-3.5" />}
        />
        <SummaryCard
          label="Peer EV/EBITDA"
          value={summary.ev_ebitda != null ? `${summary.ev_ebitda.toFixed(1)}x` : '—'}
          sub={
            summary.wacc != null
              ? `WACC: ${formatPercent(summary.wacc)}`
              : ebitdaSub
          }
          iconBg="bg-violet-50"
          iconColor="text-violet-600"
          icon={<Landmark className="h-3.5 w-3.5" />}
        />
      </div>
    </div>
  );
}
